<template>
    <div class="tag-contacts" v-if="tag">
        <span class="tag-contacts-name">{{ tag.attributes.name }}</span>
        <p class="tag-contacts-count">
            {{ $tc('tag-contacts-count', total) }}
        </p>
        <div class="tag-contacts-list" ref="list" @scroll="onScroll">
            <div class="tag-contacts-state" v-if="loading">
                <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
            </div>
            <p class="tag-contacts-state" v-else-if="!contacts.length">{{ $t('no-results-found') }}</p>
            <template v-else>
                <a
                    v-for="contact in contacts"
                    :key="contact.id"
                    href="#"
                    class="tag-contact"
                    @click.prevent="openContact(contact.id)"
                >
                    <span class="tag-contact-avatar">
                        <img :src="contact.attributes['profile-picture']" alt="" v-if="contact.attributes['profile-picture']">
                        <model-short-name :model="contact" v-else></model-short-name>
                    </span>
                    <span class="tag-contact-identity">
                        <span class="tag-contact-name">{{ displayName(contact) }}</span>
                        <span class="tag-contact-detail" v-if="displayDetail(contact)">{{ displayDetail(contact) }}</span>
                    </span>
                </a>
                <div class="tag-contacts-state" v-if="loadingMore">
                    <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>

// Internal components
import ModelShortName from '../ModelShortName.vue';

// Internal helpers
import Contact from '@/helpers/Contact.js';

// Import external libraries
import { mapActions } from 'vuex';

const PAGE_SIZE        = 25;
const SCROLL_THRESHOLD = 80;

export default {
    components: { ModelShortName },

    props: {
        tag: {
            type:    Object,
            default: null
        }
    },

    data () {
        return {
            contacts:    [],
            meta:        null,
            page:        1,
            loading:     false,
            loadingMore: false
        };
    },

    computed: {
        /**
         * Reload the contacts when another tag is selected or when the selected tag is renamed
         * (the contacts are filtered by the tag name).
         */
        tagKey () {
            return this.tag ? `${this.tag.id}|${this.tag.attributes.name}` : null;
        },

        total () {
            return this.meta ? this.meta.total : (this.tag.attributes['contacts-count'] || 0);
        },

        hasMore () {
            return !!this.meta && this.meta['current-page'] < this.meta['last-page'];
        }
    },

    watch: {
        tagKey: {
            immediate: true,
            handler () {
                this.loadFirstPage();
            }
        }
    },

    methods: {
        ...mapActions('core/contacts', ['getByTag', 'getSingle']),

        displayName:   contact => Contact.displayName(contact),
        displayDetail: contact => Contact.displayDetail(contact),

        /**
         * Load the first page of contacts of the selected tag.
         */
        loadFirstPage () {
            this.contacts = [];
            this.meta     = null;
            this.page     = 1;

            if (!this.tag) {
                return;
            }

            this.loading = true;
            this.fetchPage().then(() => {
                this.loading = false;
                this.fillList();
            });
        },

        /**
         * Load the next page of contacts when the list is scrolled near its end.
         *
         * @param   {Event}     event   The scroll event.
         */
        onScroll (event) {
            const list = event.target;

            if (list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_THRESHOLD) {
                this.loadNextPage();
            }
        },

        /**
         * Load the next page of contacts, if there is one.
         */
        loadNextPage () {
            if (!this.hasMore || this.loading || this.loadingMore) {
                return;
            }

            this.page++;
            this.loadingMore = true;
            this.fetchPage().then(() => {
                this.loadingMore = false;
                this.fillList();
            });
        },

        /**
         * Keep loading while the list is too short to be scrolled (tall screens), otherwise the next pages would never load.
         */
        fillList () {
            this.$nextTick(() => {
                const list = this.$refs.list;

                if (list && list.scrollHeight <= list.clientHeight) {
                    this.loadNextPage();
                }
            });
        },

        /**
         * Fetch the current page, ignoring the answer if another tag was selected in the meantime.
         *
         * @return  {Promise}
         */
        fetchPage () {
            const tagId = this.tag.id;

            return this.getByTag({
                tag:  this.tag.attributes.name,
                page: { number: this.page, size: PAGE_SIZE }
            })
                .then(({ contacts, meta }) => {
                    if (this.tag && this.tag.id === tagId) {
                        this.contacts = this.contacts.concat(contacts);
                        this.meta     = meta;
                    }
                })
                .catch(() => {
                    if (this.tag && this.tag.id === tagId) {
                        this.meta = null;
                    }
                });
        },

        /**
         * Open the page of a contact.
         *
         * @param   {string}    contactId   The id of the contact.
         */
        openContact (contactId) {
            this.getSingle({ id: contactId }).then(() => {
                this.$router.push({ name: `contact:${this.$i18n.locale}` });
            });
        }
    }
};
</script>
